import React, { Component } from 'react';
import { Button } from 'react-bootstrap';

export function formatSystemReport(report) {
    return [
        ['Orbis — System information', report.scope, 'Collected: ' + report.collectedAt].join('\n'),
        ...report.sections.map(section => section.title + '\n' +
            section.rows.map(row => row.label + ': ' + row.value).join('\n')),
        report.note
    ].join('\n\n');
}

// Clipboard API needs HTTPS; execCommand also supports local HTTP installations.
export async function copySystemReport(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        try { await navigator.clipboard.writeText(text); return; }
        catch (error) { /* Try the local HTTP / denied-permission fallback. */ }
    }
    const focused = document.activeElement;
    const input = document.createElement('textarea');
    input.value = text;
    input.setAttribute('readonly', '');
    input.setAttribute('aria-label', 'System information to copy');
    input.style.cssText = 'position:fixed;left:0;top:0;width:1px;height:1px;opacity:0';
    // Keep the temporary input inside the modal's focus trap.
    const modal = focused && focused.closest ? focused.closest('[role="dialog"]') : null;
    (modal || document.body).appendChild(input);
    try {
        input.focus();
        input.select();
        if (!document.execCommand || !document.execCommand('copy')) throw new Error('Copy denied');
    } finally {
        input.remove();
        if (focused && focused.focus) focused.focus();
    }
}

export default class SystemInformationPanel extends Component {
    state = { report: null, loading: true, error: '', copyStatus: '', manualCopy: false };
    mounted = false;
    controller = null;

    componentDidMount() { this.mounted = true; this.load(); }
    componentWillUnmount() {
        this.mounted = false;
        if (this.controller) this.controller.abort();
    }

    load = async () => {
        if (this.controller) this.controller.abort();
        const controller = new AbortController();
        this.controller = controller;
        this.setState({ loading: true, error: '', report: null, copyStatus: '', manualCopy: false });
        const timeout = setTimeout(() => controller.abort(), 15000);
        try {
            const response = await fetch('system_info', {
                method: 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal
            });
            if (!response.ok) throw new Error(response.status === 403
                ? 'Your session cannot access system information. Sign in as an administrator.'
                : 'System information could not be loaded. Please try again.');
            const report = await response.json();
            if (!report || typeof report.collectedAt !== 'string' || isNaN(Date.parse(report.collectedAt)) ||
                typeof report.scope !== 'string' || typeof report.note !== 'string' ||
                !Array.isArray(report.sections) || report.sections.length === 0 ||
                !report.sections.every(section => section && typeof section.id === 'string' &&
                    typeof section.title === 'string' && Array.isArray(section.rows) &&
                    section.rows.every(row => row && typeof row.label === 'string' && typeof row.value === 'string'))) {
                throw new Error('The server returned an invalid system report. Please try again.');
            }
            if (this.mounted && this.controller === controller) this.setState({ report, loading: false });
        } catch (error) {
            if (this.mounted && this.controller === controller) {
                this.setState({ loading: false, error: error.name === 'AbortError'
                    ? 'The system information request timed out. Please try again.' : error.message });
            }
        } finally { clearTimeout(timeout); }
    }

    copy = async () => {
        const report = this.state.report;
        if (!report) return;
        try {
            await copySystemReport(formatSystemReport(report));
            if (this.mounted) this.setState({ copyStatus: 'Copied to clipboard.', manualCopy: false });
        } catch (error) {
            if (this.mounted) this.setState({
                copyStatus: 'Clipboard access was blocked. Select and copy the report below.', manualCopy: true
            });
        }
    }

    render() {
        const { report, loading, error, copyStatus, manualCopy } = this.state;
        return <div className="systemInformation">
            <div className="systemInformationContent" aria-busy={loading}>
                <p className="systemInformationIntro">Hardware information for the server running Orbis.</p>
                {loading && <p role="status">Loading system information…</p>}
                {error && <div className="systemInformationError" role="alert">
                    <p>{error}</p><Button variant="outline-secondary" onClick={this.load}>Try again</Button>
                </div>}
                {report && <>
                    <p className="systemInformationTimestamp">Snapshot: {new Date(report.collectedAt).toLocaleString()}</p>
                    <div className="systemInformationGrid">
                        {report.sections.map(section => <section className="systemInformationCard" key={section.id}
                            aria-labelledby={'hardware-' + section.id}>
                            <h2 id={'hardware-' + section.id}>{section.title}</h2>
                            <dl>{section.rows.map(row => <div className="systemInformationRow" key={row.label}>
                                <dt>{row.label}</dt><dd className={row.value === 'Unavailable' ? 'systemUnavailable' : ''}>{row.value}</dd>
                            </div>)}</dl>
                        </section>)}
                    </div>
                    <p className="systemInformationNote">{report.note}</p>
                </>}
                {manualCopy && <textarea className="systemManualCopy" aria-label="System report"
                    readOnly value={formatSystemReport(report)} onFocus={event => event.target.select()} />}
            </div>
            <div className="systemInformationActions">
                <span role="status" aria-live="polite">{copyStatus}</span>
                <Button onClick={this.copy} disabled={!report || loading}>Copy all</Button>
            </div>
        </div>;
    }
}
